import * as THREE from 'three';
import type { Fit, SceneGraph, SceneNode, Vec3 } from '../geometry';
import { buildWallBracket } from './bracket';
import { resolveMaterial } from './materials';

/** Резолвер модели-детали по meshId (клон glTF) или null (тогда плейсхолдер). */
export type ModelResolver = (meshId: string) => THREE.Object3D | null;

// Плейсхолдеры (масштабируются трансформом узла): куб и цилиндр вдоль оси X (вал).
const UNIT_BOX = new THREE.BoxGeometry(1, 1, 1);
const UNIT_CYL_X = new THREE.CylinderGeometry(0.5, 0.5, 1, 24).rotateZ(Math.PI / 2);

function placeholderGeometry(shape: SceneNode['shape']): THREE.BufferGeometry {
  return shape === 'cylinderX' ? UNIT_CYL_X : UNIT_BOX;
}

/**
 * Бокс, чьи UV равны физическому размеру в МЕТРАХ (1 UV = 1 м). Тайлинг задаёт
 * материал через texture.repeat (повторов на метр) — как в старом 3D
 * (BaseMaterial.cs: масштаб = 1/UVScale × localScale). Без растяжения.
 */
function boxWithWorldUV(size: Vec3): THREE.BufferGeometry {
  const g = new THREE.BoxGeometry(1, 1, 1);
  const uv = g.getAttribute('uv') as THREE.BufferAttribute;
  for (let i = 0; i < uv.count; i++) uv.setXY(i, uv.getX(i) * size[0], uv.getY(i) * size[1]);
  uv.needsUpdate = true;
  return g;
}

// Шариковая цепь: шарики Ø4мм вдоль оси Y (натуральный размер, не масштабируются).
const BEAD_RADIUS = 0.002; // 4 мм диаметр
const BEAD_PITCH = 0.0045; // шаг шариков
const BEAD_GEO = new THREE.SphereGeometry(BEAD_RADIUS, 10, 8);

function buildBeadChain(lengthY: number, material: THREE.Material): THREE.InstancedMesh {
  const n = Math.max(1, Math.round(Math.abs(lengthY) / BEAD_PITCH));
  const mesh = new THREE.InstancedMesh(BEAD_GEO, material, n);
  const m = new THREE.Matrix4();
  for (let i = 0; i < n; i++) {
    m.makeTranslation(0, -lengthY / 2 + (i + 0.5) * BEAD_PITCH, 0);
    mesh.setMatrixAt(i, m);
  }
  mesh.instanceMatrix.needsUpdate = true;
  return mesh;
}

function applyMaterial(obj: THREE.Object3D, material: THREE.Material): void {
  obj.traverse((o) => {
    if (o instanceof THREE.Mesh) o.material = material;
  });
}

/**
 * Подогнать реальную модель под размер: рецентрировать в начало координат и
 * применить масштаб по режиму `fit` (стретч вдоль оси / равномерно). Возвращает
 * группу-обёртку, уже отмасштабированную; внешний трансформ задаёт положение.
 */
function fitModel(model: THREE.Object3D, fit: Fit): THREE.Group {
  const box = new THREE.Box3().setFromObject(model);
  const size = box.getSize(new THREE.Vector3());
  const center = box.getCenter(new THREE.Vector3());
  model.position.sub(center); // центр bbox → в начало координат

  const wrapper = new THREE.Group();
  wrapper.add(model);

  if (fit.mode === 'stretch') {
    const nativeAxis = fit.axis === 'x' ? size.x : fit.axis === 'y' ? size.y : size.z;
    const k = fit.length / (nativeAxis || 1);
    if (fit.axis === 'x') {
      wrapper.scale.x = k;
      if (fit.crossSection) {
        wrapper.scale.y = fit.crossSection[0] / (size.y || 1);
        wrapper.scale.z = fit.crossSection[1] / (size.z || 1);
      }
    } else if (fit.axis === 'y') wrapper.scale.y = k;
    else wrapper.scale.z = k;
  } else {
    const maxN = Math.max(size.x, size.y, size.z) || 1;
    wrapper.scale.setScalar(fit.size / maxN);
  }
  return wrapper;
}

function buildObject(node: SceneNode, resolveModel?: ModelResolver): THREE.Object3D {
  let obj: THREE.Object3D;
  let useNodeScale = true; // плейсхолдер/модель-без-fit масштабируются трансформом узла
  if (node.meshId !== undefined) {
    const material = resolveMaterial(node.materialId);
    const loaded = resolveModel?.(node.meshId) ?? null;
    if (loaded) {
      applyMaterial(loaded, material);
      if (node.fit) {
        obj = fitModel(loaded, node.fit); // масштаб уже внутри обёртки
        useNodeScale = false;
      } else {
        obj = loaded;
      }
    } else if (node.shape === 'beadChain') {
      obj = buildBeadChain(node.transform.scale[1], material); // длина по scale.y
      useNodeScale = false; // шарики натурального размера
    } else if (node.shape === 'wallBracket') {
      obj = buildWallBracket(node.transform.scale, material); // [толщина, высота, вылет]
      useNodeScale = false; // деталь строится в натуральную величину
    } else if (node.uvWorld) {
      obj = new THREE.Mesh(boxWithWorldUV(node.transform.scale), material);
    } else {
      obj = new THREE.Mesh(placeholderGeometry(node.shape), material);
    }
  } else {
    obj = new THREE.Group();
  }

  obj.name = node.id;
  const t = node.transform;
  obj.position.set(t.position[0], t.position[1], t.position[2]);
  obj.rotation.set(t.rotation[0], t.rotation[1], t.rotation[2]);
  if (useNodeScale) obj.scale.set(t.scale[0], t.scale[1], t.scale[2]);

  for (const child of node.children) obj.add(buildObject(child, resolveModel));
  return obj;
}

/** SceneGraph (нейтральный) → three.js группа; модели по meshId через resolveModel. */
export function composeScene(graph: SceneGraph, resolveModel?: ModelResolver): THREE.Group {
  const group = new THREE.Group();
  group.name = 'model';
  group.add(buildObject(graph.root, resolveModel));
  return group;
}
